import { useCallback, useEffect, useRef, useState } from "react";
import type { Client } from "@stomp/stompjs";
import {
  createClientChatSocket,
  registerClientUser,
  sendClientMessage,
  subscribeClientTopic,
} from "../api/clientChatWebSocket";
import {
  chatReconnectFailedMessage,
  chatReconnectIntervalMs,
  chatReconnectMaxAttempts,
} from "../../config/config.js";
import type { ChatMessageView, ChatSocketPayload } from "../types";
import {
  capMessages,
  extractOperatorRecipient,
  formatDateTimeLabel,
  normalizeSocketContent,
  resolveUserId,
  socketHistoryToViews,
  socketPayloadToView,
} from "../utils/messages";
import { getUserFacingChatError } from "../utils/chatErrors";

function parseSocketBody(body: string): ChatSocketPayload | null {
  try {
    const parsed = JSON.parse(body) as ChatSocketPayload;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function getMessageType(payload: ChatSocketPayload): string {
  return String(payload.messageType ?? payload.messageTypeEnum ?? "").trim().toUpperCase();
}

function isChatSocketOpen(client: Client | null | undefined): boolean {
  if (!client?.connected) return false;
  const ws = client.webSocket;
  if (!ws) return false;
  return ws.readyState === WebSocket.OPEN;
}

export interface UseClientChatResult {
  uuid: string;
  userId: string;
  messages: ChatMessageView[];
  messageText: string;
  setMessageText: (value: string) => void;
  isReady: boolean;
  isSending: boolean;
  isConnecting: boolean;
  error: string | null;
  sendMessage: () => void;
}

export function useClientChat(uuidProp: string): UseClientChatResult {
  const uuid = String(uuidProp ?? "").trim();
  const [userId, setUserId] = useState("");
  const [messages, setMessages] = useState<ChatMessageView[]>([]);
  const [messageText, setMessageText] = useState("");
  const [isReady, setIsReady] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clientRef = useRef<Client | null>(null);
  const userIdRef = useRef("");
  const operatorRecipientRef = useRef("");
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const messageIdRef = useRef(0);
  const currentTopicRef = useRef("");
  const connectionFailedRef = useRef(false);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disposedRef = useRef(false);
  const handlingConnectionLossRef = useRef(false);
  const suppressConnectionLossRef = useRef(false);
  const pausedForBackgroundRef = useRef(false);
  const wasEverReadyRef = useRef(false);
  const lastResumeAtRef = useRef(0);
  const processIncomingPayloadRef = useRef<(payload: ChatSocketPayload, client?: Client) => void>(
    () => {}
  );

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const stopConnection = useCallback((options?: { suppressLossHandler?: boolean }) => {
    if (options?.suppressLossHandler) {
      suppressConnectionLossRef.current = true;
    }

    unsubscribeRef.current?.();
    unsubscribeRef.current = null;

    const activeClient = clientRef.current;
    clientRef.current = null;
    if (activeClient) {
      void activeClient.deactivate({ force: true }).finally(() => {
        if (options?.suppressLossHandler) {
          suppressConnectionLossRef.current = false;
        }
      });
    } else if (options?.suppressLossHandler) {
      suppressConnectionLossRef.current = false;
    }
  }, []);

  const appendMessage = useCallback((message: ChatMessageView) => {
    setMessages((prev) => capMessages([...prev, message]));
  }, []);

  const mergeMessages = useCallback((incoming: ChatMessageView[]) => {
    if (incoming.length === 0) return;

    setMessages((prev) => {
      const next = [...prev];
      const indexById = new Map(next.map((item, index) => [item.id, index]));

      const normalized = (value: string): string =>
        String(value ?? "")
          .trim()
          .toLowerCase();

      const findMatchingDuplicateIndex = (item: ChatMessageView): number => {
        const incomingText = normalized(item.text);
        if (!incomingText) return -1;

        return next.findIndex((existing) => {
          if (existing.isOutgoing !== item.isOutgoing) return false;
          if (normalized(existing.text) !== incomingText) return false;

          const existingId = String(existing.id);

          if (item.isOutgoing) {
            if (existingId.startsWith("local-")) return true;
            if (
              existing.timeLabel &&
              item.timeLabel &&
              existing.timeLabel === item.timeLabel
            ) {
              return true;
            }
            return false;
          }

          if (
            existing.timeLabel &&
            item.timeLabel &&
            existing.timeLabel === item.timeLabel
          ) {
            return normalized(existing.sender) === normalized(item.sender);
          }

          return normalized(existing.sender) === normalized(item.sender);
        });
      };

      for (const item of incoming) {
        const existingIndex = indexById.get(item.id);
        if (typeof existingIndex === "number") {
          next[existingIndex] = item;
        } else {
          const duplicateIndex = findMatchingDuplicateIndex(item);
          if (duplicateIndex >= 0) {
            const duplicateId = next[duplicateIndex].id;
            next[duplicateIndex] = item;
            indexById.delete(duplicateId);
            indexById.set(item.id, duplicateIndex);
          } else {
            next.push(item);
            indexById.set(item.id, next.length - 1);
          }
        }
      }

      return capMessages(next);
    });
  }, []);

  const subscribeToTopic = useCallback((client: Client, topic: string) => {
    const normalizedTopic = String(topic ?? "").trim();
    if (!normalizedTopic) return;

    unsubscribeRef.current?.();
    unsubscribeRef.current = subscribeClientTopic(
      client,
      normalizedTopic,
      uuid,
      (message) => {
        const payload = parseSocketBody(message.body);
        if (!payload) return;
        processIncomingPayloadRef.current(payload, client);
      }
    );
    currentTopicRef.current = normalizedTopic;
  }, [uuid]);

  const processIncomingPayload = useCallback(
    (payload: ChatSocketPayload, client?: Client) => {
      const messageType = getMessageType(payload);
      if (messageType === "ERROR") {
        const message = String(payload.errorMessage ?? "").trim();
        setError(message || "Ошибка сервиса чата");
        return;
      }

      const nextRecipient = extractOperatorRecipient(payload);
      if (nextRecipient) {
        operatorRecipientRef.current = nextRecipient;
      }

      const resolvedUserId = resolveUserId(payload);
      if (resolvedUserId && resolvedUserId !== userIdRef.current) {
        userIdRef.current = resolvedUserId;
        setUserId(resolvedUserId);
      }

      if (client && resolvedUserId && resolvedUserId !== currentTopicRef.current) {
        subscribeToTopic(client, resolvedUserId);
      }

      const content = normalizeSocketContent(payload.content);
      const historyItems = Array.isArray(content?.messages) ? content.messages : [];

      if (messageType === "SEND_MESSAGE_USER") {
        const isResultOk = content && typeof content === "object" && content.result === true;
        if (!isResultOk) {
          setError("Не удалось отправить сообщение");
        }
      }

      if (historyItems.length > 0) {
        const sortedHistory = [...historyItems].sort((left, right) => {
          const leftTime = new Date(String(left.createdAt ?? "")).getTime();
          const rightTime = new Date(String(right.createdAt ?? "")).getTime();
          if (Number.isNaN(leftTime) || Number.isNaN(rightTime)) return 0;
          return leftTime - rightTime;
        });

        mergeMessages(socketHistoryToViews(sortedHistory, userIdRef.current || uuid));

        if (messageType === "REGISTER_USER" || messageType === "SEND_MESSAGE_USER") {
          return;
        }
      }

      const view = socketPayloadToView(
        payload,
        userIdRef.current || uuid,
        `ws-${messageIdRef.current++}`
      );
      if (view) appendMessage(view);
    },
    [appendMessage, mergeMessages, subscribeToTopic, uuid]
  );
  processIncomingPayloadRef.current = processIncomingPayload;

  const scheduleReconnect = useCallback(() => {
    if (disposedRef.current || connectionFailedRef.current || pausedForBackgroundRef.current) {
      return;
    }

    reconnectAttemptRef.current += 1;
    if (reconnectAttemptRef.current > chatReconnectMaxAttempts) {
      connectionFailedRef.current = true;
      setError(chatReconnectFailedMessage);
      setIsConnecting(false);
      setIsReady(false);
      return;
    }

    setIsConnecting(true);
    setIsReady(false);
    setError(null);
    clearReconnectTimer();

    const delay =
      typeof document !== "undefined" && document.visibilityState === "hidden"
        ? 0
        : chatReconnectIntervalMs;

    const runReconnect = () => {
      reconnectTimerRef.current = null;
      if (
        disposedRef.current ||
        connectionFailedRef.current ||
        pausedForBackgroundRef.current
      ) {
        return;
      }
      startConnectionRef.current();
    };

    if (delay === 0) {
      runReconnect();
      return;
    }

    reconnectTimerRef.current = setTimeout(runReconnect, delay);
  }, [clearReconnectTimer]);

  const handleConnectionLost = useCallback(() => {
    if (
      disposedRef.current ||
      connectionFailedRef.current ||
      handlingConnectionLossRef.current ||
      suppressConnectionLossRef.current ||
      pausedForBackgroundRef.current
    ) {
      return;
    }
    handlingConnectionLossRef.current = true;
    stopConnection();
    scheduleReconnect();
  }, [scheduleReconnect, stopConnection]);

  const startConnectionRef = useRef<() => void>(() => {});

  const startConnection = useCallback(() => {
    if (disposedRef.current || connectionFailedRef.current) return;

    handlingConnectionLossRef.current = false;
    stopConnection({ suppressLossHandler: true });
    setIsConnecting(true);
    setError(null);

    const client = createClientChatSocket({
      authHeader: uuid,
      onConnect: (connectedClient) => {
        if (disposedRef.current) return;
        reconnectAttemptRef.current = 0;
        clientRef.current = connectedClient;

        subscribeToTopic(connectedClient, uuid);
        registerClientUser(connectedClient, uuid);

        wasEverReadyRef.current = true;
        setIsReady(true);
        setIsConnecting(false);
      },
      onDisconnect: () => {
        handleConnectionLost();
      },
      onError: () => {
        handleConnectionLost();
      },
    });

    clientRef.current = client;
    client.activate();
  }, [handleConnectionLost, stopConnection, subscribeToTopic, uuid]);

  startConnectionRef.current = startConnection;

  const pauseConnectionForBackground = useCallback(() => {
    if (disposedRef.current || pausedForBackgroundRef.current) return;

    pausedForBackgroundRef.current = true;
    clearReconnectTimer();
    handlingConnectionLossRef.current = false;

    if (clientRef.current) {
      stopConnection({ suppressLossHandler: true });
    }

    setIsReady(false);
    setIsConnecting(false);
  }, [clearReconnectTimer, stopConnection]);

  const resumeConnectionIfNeeded = useCallback(() => {
    if (disposedRef.current) return;

    const now = Date.now();
    if (now - lastResumeAtRef.current < 100) return;
    lastResumeAtRef.current = now;

    const wasBackgroundPause = pausedForBackgroundRef.current;
    pausedForBackgroundRef.current = false;

    if (isChatSocketOpen(clientRef.current)) {
      setIsReady(true);
      setIsConnecting(false);
      setError(null);
      reconnectAttemptRef.current = 0;
      connectionFailedRef.current = false;
      return;
    }

    if (connectionFailedRef.current || wasBackgroundPause || wasEverReadyRef.current) {
      connectionFailedRef.current = false;
      reconnectAttemptRef.current = 0;
      setError(null);
    }

    clearReconnectTimer();
    handlingConnectionLossRef.current = false;
    startConnectionRef.current();
  }, [clearReconnectTimer]);

  useEffect(() => {
    if (!uuid) {
      setError("Не указан UUID (маршрут /chat/:uuid)");
      return;
    }

    disposedRef.current = false;
    connectionFailedRef.current = false;
    reconnectAttemptRef.current = 0;
    pausedForBackgroundRef.current = false;
    wasEverReadyRef.current = false;
    currentTopicRef.current = "";
    userIdRef.current = uuid;
    setUserId(uuid);
    setMessages([]);
    operatorRecipientRef.current = "";
    setError(null);

    startConnection();

    return () => {
      disposedRef.current = true;
      clearReconnectTimer();
      suppressConnectionLossRef.current = true;
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
      clientRef.current?.deactivate();
      clientRef.current = null;
      setIsReady(false);
    };
  }, [clearReconnectTimer, startConnection, uuid]);

  useEffect(() => {
    const onOffline = () => {
      handleConnectionLost();
    };

    const onOnline = () => {
      resumeConnectionIfNeeded();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        pauseConnectionForBackground();
        return;
      }
      resumeConnectionIfNeeded();
    };

    const onPageHide = () => {
      pauseConnectionForBackground();
    };

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        resumeConnectionIfNeeded();
      }
    };

    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [handleConnectionLost, pauseConnectionForBackground, resumeConnectionIfNeeded]);

  const sendMessage = useCallback(() => {
    const text = messageText.trim();
    const sender = userIdRef.current || uuid;
    const client = clientRef.current;

    if (!text || !sender || !isChatSocketOpen(client)) return;

    setIsSending(true);
    setError(null);

    try {
      sendClientMessage(client, text, uuid);

      appendMessage({
        id: `local-${messageIdRef.current++}`,
        text,
        sender,
        isOutgoing: true,
        timeLabel: formatDateTimeLabel(new Date()),
      });
      setMessageText("");
    } catch (sendError) {
      setError(getUserFacingChatError(sendError));
    } finally {
      setIsSending(false);
    }
  }, [appendMessage, messageText, uuid]);

  return {
    uuid,
    userId,
    messages,
    messageText,
    setMessageText,
    isReady,
    isSending,
    isConnecting,
    error,
    sendMessage,
  };
}
